/* eslint-disable no-nested-ternary, no-restricted-syntax, react/no-array-index-key, jsx-a11y/media-has-caption, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/interactive-supports-focus */
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  X,
  MessageSquare,
  User as UserIcon,
  Bot,
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { SearchParam } from '@common/ts/enums';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import { formatMessageTimestamp } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import useMediaQuery from '@/hooks/useMediaQuery';
import useTranslation from '@/hooks/useTranslation';
import FetchErrorState from '@/components/ui/FetchErrorState';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { useGetInterviewQuestionTranscriptsPaginated } from '@/reactQuery/get';
import useCategoryStore from '@/store/CategoryStore';
import { ECategoryMode } from '@/enums';
import getTextColor from '@/utils/getTextColorContrast';
import { getCategoryColor } from '@/utils/categoriesColorList';
import useQuestionTranslation from '@/hooks/useQuestionTranslation';
import TranscriptsTranslationDropdown from '@/pages/projects/single-project/Content/transcripts_table_data/TranscriptsTranslationDropdown';
import type { Transcripts, Category, CategoryApiResponse, SelectedCategory } from '@/types';
import SingleCategoryCell from './SingleCategoryCell';
import MultipleCategoryCell from './MultipleCategoryCell';
import { parseMessages } from './transcriptHelpers';

type SortOption = 'newest' | 'oldest' | 'respondent';

// Width constraints for the resizable Sheet. Minimum keeps the card
// layout legible; maximum caps well below a typical 1440 viewport so
// the TranscriptPanel (~500px on the right) can sit alongside without
// overlap. The drag handle persists the chosen width to localStorage.
const PANEL_MIN_WIDTH = 500;
const PANEL_MAX_WIDTH = 1100;
const PANEL_DEFAULT_WIDTH = 720;
const PANEL_WIDTH_KEY = 'responsesPanel:width';

function clampPanelWidth(value: number): number {
  return Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, value));
}

export interface ResponseCardProps {
  transcript: Transcripts;
  categoryData?: CategoryApiResponse;
  questionId?: string;
  projectId?: string;
  onOpenTranscript: (conversationId: string) => void;
}

export function ResponseCard({
  transcript,
  categoryData,
  questionId,
  projectId,
  onOpenTranscript,
}: ResponseCardProps) {
  const { lang } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const { isUserViewer } = useWorkspaceRoles();
  const categoryType = useCategoryStore((state) => state.categoryType);

  const {
    conversation_id,
    questions,
    messages: structuredMessages,
    category,
    media,
    started,
    respondent_index,
  } = transcript;

  // Prefer the structured per-message shape (each message carries its
  // own data array — audio/video/image attached to the message that
  // produced them). Fall back to the legacy string parser for older
  // API responses that don't carry the `messages` field. Both shapes
  // are normalised to { role, text, data } for the renderer below.
  const messages = useMemo(() => {
    if (structuredMessages && structuredMessages.length > 0) {
      return structuredMessages.map((m) => ({
        // Normalise the backend role to the labels the renderer uses
        // (the existing isInterviewer check expects them). Backend
        // emits only lowercase 'assistant' / 'user' per MessageRole
        // enum; SYSTEM messages are filtered out server-side.
        role: m.role === 'assistant' ? 'Interviewer' : 'Participant',
        text: m.text,
        data: m.data,
      }));
    }
    return parseMessages(questions || '').map((m) => ({
      role: m.role,
      text: m.text,
      data: [] as NonNullable<typeof transcript.messages>[number]['data'],
    }));
  }, [structuredMessages, questions]);

  // Preview: first participant text, truncated.
  // For media-only responses the message.text is empty — fall back to
  // the first audio OR video item's transcription_text so the preview
  // still shows something meaningful. Images intentionally do not
  // contribute to the preview because their transcription_text is an
  // AI vision-model description of the image, not what the respondent
  // said.
  const firstParticipant = messages.find(
    (msg) => msg.role !== 'Interviewer' && msg.role !== 'Assistant',
  );
  const firstParticipantMediaTranscription = firstParticipant?.data?.find(
    (d) => (d.type === 'audio' || d.type === 'video') && !!d.transcription_text,
  )?.transcription_text;
  const previewText = firstParticipant?.text || firstParticipantMediaTranscription || '';
  const truncated = previewText.length > 300 ? `${previewText.slice(0, 300)}…` : previewText;
  const hasMultipleMessages = messages.length > 1;

  // Normalise category into an array for rendering, and into a single
  // value for the SingleCategoryCell editor when in SINGLE mode.
  const categories: Category[] = Array.isArray(category) ? category : category ? [category] : [];
  const firstCategory = categories[0];
  const categoryDataSingle = categoryData?.categories;
  const categoryDataMultiple = categoryData?.categories_multi;

  // Map category labels to green palette colors based on their position
  // in the full category list so badges match the chart.
  const categoryColorMap = useMemo(() => {
    const allCats = categoryDataSingle || categoryDataMultiple || [];
    const map = new Map<string, string>();
    allCats.forEach((cat, i) => map.set(cat.label, getCategoryColor(i)));
    return map;
  }, [categoryDataSingle, categoryDataMultiple]);

  const canEditCategory =
    !isUserViewer && !!questionId && !!projectId && !!categoryType && categories.length > 0;

  return (
    <div className="rounded-xl border border-neutral-200/80 bg-white p-4 flex flex-col gap-3">
      {/* Card header: respondent badge + date + categories (with edit
          pencil when the user has edit rights) */}
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => onOpenTranscript(conversation_id)}
          className="flex items-center gap-2 group shrink-0"
          title={lang.get('msg.showFullConversation')}
        >
          <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors">
            <UserIcon className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-semibold text-neutral-700 group-hover:text-neutral-900 transition-colors">
              {lang.get('msg.respondent')} #{respondent_index ?? '—'}
            </span>
            {formatMessageTimestamp(started) && (
              <span className="flex items-center gap-1 text-[11px] text-neutral-400">
                <Clock className="h-2.5 w-2.5" />
                {formatMessageTimestamp(started)}
              </span>
            )}
          </div>
        </button>

        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 justify-end min-w-0">
            {categories.map((cat: Category) => {
              // Defensive null check: backend has been observed to return
              // null entries in the category array for some questions
              // (e.g. questions with no categorisation set up but with
              // legacy quantification rows). Without this guard, `cat.label`
              // below crashes ResponseCard and takes the whole page down.
              if (!cat?.label) return null;
              const bgColor = categoryColorMap.get(cat.label) || cat.color || '#096846';
              return (
                <span
                  key={cat.label}
                  style={{
                    backgroundColor: bgColor,
                    color: getTextColor(bgColor),
                  }}
                  className="text-xs font-medium px-2.5 py-0.5 rounded-full"
                >
                  {cat.label}
                </span>
              );
            })}
            {/* Inline category editor — pencil icon. Reuses the
                pre-redesign cells; gated behind workspace edit rights. */}
            {canEditCategory &&
              categoryType === ECategoryMode.SINGLE &&
              categoryDataSingle &&
              firstCategory && (
                <SingleCategoryCell
                  rowId={conversation_id}
                  categoryData={categoryDataSingle}
                  categories={firstCategory}
                  questionID={questionId}
                  conversation_id={conversation_id}
                  project_id={projectId}
                  onLoadingStart={() => setIsEditingCategory(true)}
                  onLoadingEnd={() => setIsEditingCategory(false)}
                />
              )}
            {canEditCategory && categoryType === ECategoryMode.MULTIPLE && categoryDataMultiple && (
              <MultipleCategoryCell
                categoryData={categoryDataMultiple}
                categories={categories}
                questionID={questionId}
                conversation_id={conversation_id}
                project_id={projectId}
                onLoadingStart={() => setIsEditingCategory(true)}
                onLoadingEnd={() => setIsEditingCategory(false)}
              />
            )}
            {isEditingCategory && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
          </div>
        )}
      </div>

      {/* Collapsed: preview of first answer */}
      {!isExpanded && <p className="text-sm leading-relaxed text-neutral-700">{truncated}</p>}

      {/* Expanded: full Q&A back-and-forth */}
      {isExpanded && (
        <div className="flex flex-col gap-2">
          {messages.map((msg, idx) => {
            const isInterviewer = msg.role === 'Interviewer' || msg.role === 'Assistant';
            const audioInMessage = (msg.data || []).filter((d) => d.type === 'audio' && d.url);
            const videoInMessage = (msg.data || []).filter((d) => d.type === 'video' && d.url);
            const imagesInMessage = (msg.data || []).filter((d) => d.type === 'image' && d.url);
            return (
              <div
                key={idx}
                // Match the TranscriptMessage bubble style so the same
                // conversation looks consistent whether viewed inline in
                // the response card or in the side panel. Respondent
                // messages get a visible green tint + border; interviewer
                // messages stay neutral.
                className={`flex gap-2.5 py-2 px-3 rounded-lg ${
                  isInterviewer ? '' : 'bg-primary/15 border border-primary/30'
                }`}
              >
                <div
                  className={`flex items-center justify-center h-5 w-5 rounded-full shrink-0 mt-0.5 ${isInterviewer ? 'bg-neutral-100' : 'bg-primary/20'}`}
                >
                  {isInterviewer ? (
                    <Bot className="h-3 w-3 text-neutral-400" />
                  ) : (
                    <UserIcon className="h-3 w-3 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide ${isInterviewer ? 'text-neutral-400' : 'text-primary'}`}
                  >
                    {isInterviewer ? lang.get('msg.interviewer') : lang.get('msg.respondent')}
                  </span>
                  {msg.text && (
                    <p className="text-sm leading-relaxed text-neutral-700 mt-0.5">{msg.text}</p>
                  )}
                  {/* Inline media: audio/video render directly below the
                      message text they belong to. Images render as small
                      thumbnails (full lightbox lives in the transcript
                      panel — keep this card focused on the response). */}
                  {audioInMessage.length > 0 && (
                    <div className="mt-2 flex flex-col gap-2">
                      {audioInMessage.map((item, audioIdx) => (
                        <div key={item.file_id || `audio-${audioIdx}`}>
                          <audio src={item.url} controls preload="none" className="w-full h-8" />
                          {item.transcription_text && (
                            <p className="text-xs text-neutral-500 mt-1 italic">
                              {item.transcription_quality?.low_confidence && (
                                <TooltipWrapper text="Low transcription confidence — the audio may be unclear; verify against the recording">
                                  <span className="inline-flex align-middle mr-1">
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                  </span>
                                </TooltipWrapper>
                              )}
                              &ldquo;{item.transcription_text}&rdquo;
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {videoInMessage.length > 0 && (
                    <div className="mt-2 flex flex-col gap-2">
                      {videoInMessage.map((item, videoIdx) => (
                        <div key={item.file_id || `video-${videoIdx}`}>
                          <video
                            src={item.url}
                            controls
                            preload="metadata"
                            className="w-full rounded-lg max-h-96"
                          />
                          {item.transcription_text && (
                            <p className="text-xs text-neutral-500 mt-1 italic">
                              &ldquo;{item.transcription_text}&rdquo;
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {imagesInMessage.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {imagesInMessage.map((item, imgIdx) => (
                        <a
                          key={item.file_id || `image-${imgIdx}`}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={item.url}
                            alt={item.label || lang.get('msg.respondentImage')}
                            className="max-w-[200px] rounded-lg object-cover border border-neutral-200 hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Expand/collapse toggle */}
      {hasMultipleMessages && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              {lang.get('msg.showLess')}
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              {lang.get('msg.showFullAnswer')}
            </>
          )}
        </button>
      )}

      {/* Legacy fallback media block — only renders for old API
          responses that don't carry per-message structured data.
          With `structuredMessages` present, media is rendered inline
          beneath each respondent message above. */}
      {!structuredMessages && media && media.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {media.map((item, idx) => {
            if (item.type === 'image') {
              return (
                <a
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={item.url}
                    alt={item.label || lang.get('msg.respondentImage')}
                    className="w-full max-w-[200px] rounded-lg object-cover border border-neutral-200 hover:opacity-80 transition-opacity cursor-pointer"
                  />
                </a>
              );
            }
            if (item.type === 'audio') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <audio key={idx} src={item.url} controls preload="none" className="w-full h-8" />
              );
            }
            if (item.type === 'video') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <video
                  key={idx}
                  src={item.url}
                  controls
                  preload="metadata"
                  className="w-full rounded-lg max-h-96"
                />
              );
            }
            return null;
          })}
        </div>
      )}

      {/* Explicit "view full transcript" action — the respondent badge
          in the header is also clickable, this gives a clearer
          affordance for researchers who want to jump straight to the
          full interview. */}
      <button
        type="button"
        onClick={() => onOpenTranscript(conversation_id)}
        className="inline-flex items-center gap-1 self-start text-xs font-medium text-primary hover:text-primary transition-colors"
      >
        <ExternalLink className="h-3 w-3" />
        {lang.get('msg.showFullConversation')}
      </button>
    </div>
  );
}

function ResponsesPanel({ categoryData }: { categoryData?: CategoryApiResponse }) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const categoryType = useCategoryStore((state) => state.categoryType);

  const { isOpen, questionId, selectedCategories, setSelectedCategories, clearCategories, close } =
    useResponsesPanelStore();
  const { open: openTranscriptPanel } = useTranscriptPanelStore();
  // At lg+ both panels can sit side-by-side (their widths add up to
  // < viewport). Below lg they would overlap, so we close ResponsesPanel
  // before opening TranscriptPanel to avoid a stacked pile of sheets.
  const isLg = useMediaQuery('(min-width: 1024px)');

  // ── Search + sort state ──────────────────────────────────────────

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounce(searchInput, 500);
  const [sortBy, setSortBy] = useState<SortOption>('respondent');
  const [showSortMenu, setShowSortMenu] = useState(false);

  // ── Pagination state ──────────────────────────────────────────────
  // Server-side pagination. The dashboard only holds the current page
  // in memory — critical for projects with 30k+ respondents.
  const PAGE_SIZE = 25;
  const [currentPage, setCurrentPage] = useState(1);

  // ── Translation ──────────────────────────────────────────────────

  const {
    activeLanguage,
    pendingLanguage,
    isTranslating,
    translatedLanguages: allTranslatedLanguages,
    unTranslatedLanguages,
    handleLanguageSelection,
    handleDeleteLanguage,
    languageParam,
  } = useQuestionTranslation({
    source: 'questions',
    questionId: questionId ?? undefined,
  });

  // ── Resolve selected category labels to IDs (server-side filter) ─
  //
  // The chips and the store use labels because that's what the user
  // sees; the server filters by IDs because they're stable across
  // label edits and translations. categoryData is the authoritative
  // mapping we received from the parent component.
  const categoryIds = useMemo<string[]>(() => {
    if (!selectedCategories.length || !categoryData) return [];
    const pool = [...(categoryData.categories || []), ...(categoryData.categories_multi || [])];
    const labelToId = new Map<string, string>();
    for (const c of pool) {
      if (c?.label && c?.id && !labelToId.has(c.label)) {
        labelToId.set(c.label, c.id.toString());
      }
    }
    return selectedCategories.map((s) => labelToId.get(s.label)).filter((id): id is string => !!id);
  }, [selectedCategories, categoryData]);

  // Map the 3 UI sort presets to the server's (sort_by, sort_direction)
  // pair. `respondent` is the server default and requires no override.
  const [sortByServer, sortDirectionServer] = useMemo<
    [string | undefined, 'asc' | 'desc' | undefined]
  >(() => {
    if (sortBy === 'newest') return ['createdAt', 'desc'];
    if (sortBy === 'oldest') return ['createdAt', 'asc'];
    return ['respondent_index', 'asc'];
  }, [sortBy]);

  // ── Data fetch — server paginated + filtered + sorted + searched ─

  const {
    data: paginatedResponse,
    isLoading,
    isError: isResponsesError,
    refetch: refetchResponses,
  } = useGetInterviewQuestionTranscriptsPaginated({
    filterId,
    categoryType,
    projectId,
    questionId: questionId ?? undefined,
    language: languageParam,
    page: currentPage,
    limit: PAGE_SIZE,
    search: debouncedSearch.trim() || undefined,
    categoryIds: categoryIds.length ? categoryIds : undefined,
    sortBy: sortByServer,
    sortDirection: sortDirectionServer,
    enabled: isOpen,
  });

  const transcripts = paginatedResponse?.data;
  const pagination = paginatedResponse?.pagination;

  // ── Resize handling ──────────────────────────────────────────────

  const [panelWidth, setPanelWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;
    const stored = window.localStorage.getItem(PANEL_WIDTH_KEY);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? clampPanelWidth(parsed) : PANEL_DEFAULT_WIDTH;
  });
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      resizeStateRef.current = { startX: e.clientX, startWidth: panelWidth };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      setIsDragging(true);
    },
    [panelWidth],
  );

  // E2 / audit F-2: bind mousemove/mouseup ONLY while dragging, with a
  // useEffect that has a guaranteed cleanup. The previous version
  // attached handlers in handleResizeStart and removed them in
  // handleMouseUp — but if the component unmounted mid-drag (e.g. user
  // closes the panel via a Sheet outside-click while holding the mouse
  // down), the listeners stayed attached to document forever, leaking
  // memory and continuing to mutate state on a dead component.
  useEffect(() => {
    if (!isDragging) return undefined;
    const handleMouseMove = (ev: MouseEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;
      // Drag LEFT grows the panel (it's anchored to the right side of
      // the viewport), so the delta is startX - currentX.
      const delta = state.startX - ev.clientX;
      setPanelWidth(clampPanelWidth(state.startWidth + delta));
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        setPanelWidth((current) => {
          window.localStorage.setItem(PANEL_WIDTH_KEY, String(current));
          return current;
        });
      } catch {
        // ignore localStorage failures (private mode / quota)
      }
      resizeStateRef.current = null;
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Defensive: restore body cursor/select if the cleanup runs
      // because the component unmounted mid-drag (handleMouseUp never
      // fired).
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  // The Sheet renders from y=0 by default, which puts its top under
  // the fixed project navbar (z-100, h=55px) and the sticky Results
  // tab bar (~56px below that). Without an offset the panel's own
  // header sits under that navigation and becomes invisible. Pushing
  // content down by 111px clears both. The Sheet still extends to
  // y=0 visually, so the navbar/tabs overlay the Sheet's blank top
  // strip — no awkward gap.
  const NAV_OFFSET = 111;
  const effectiveContentStyle: React.CSSProperties = {
    paddingTop: NAV_OFFSET,
    ...(isLg ? { width: `${panelWidth}px`, maxWidth: 'none' } : {}),
  };

  // ── Close handler: reset local state ─────────────────────────────

  const handleClose = useCallback(() => {
    close();
    setSearchInput('');
    setSortBy('respondent');
    setShowSortMenu(false);
    setCurrentPage(1);
  }, [close]);

  // Reset search + sort + pagination when switching question
  const prevQuestionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) {
      setSearchInput('');
      setSortBy('respondent');
      setCurrentPage(1);
      prevQuestionIdRef.current = questionId;
    }
  }, [questionId]);

  // Reset page to 1 whenever the visible subset changes (filters,
  // sort, search). Otherwise a user on page 10 of all responses
  // who applies a filter down to 3 results would see page 10 of 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories, debouncedSearch, sortBy]);

  // ── Category options for the chip filter ────────────────────────
  // Derived from the authoritative categoryData prop (not from the
  // current page of transcripts) so the chip list is complete and
  // stable across pages.
  const allCategoryOptions = useMemo<SelectedCategory[]>(() => {
    if (!categoryData) return [];
    const pool = [...(categoryData.categories || []), ...(categoryData.categories_multi || [])];
    const seen = new Map<string, SelectedCategory>();
    for (const c of pool) {
      if (c?.label && !seen.has(c.label)) {
        seen.set(c.label, { label: c.label, value: c.label });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [categoryData]);

  // The page's transcripts and pagination metadata are the server
  // response — filter/sort/search/paginate already applied there.
  const pageTranscripts = transcripts ?? [];
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const handleOpenTranscript = (conversationId: string) => {
    // Only close ResponsesPanel on narrow widths where both sheets
    // would overlap. At lg+ both panels coexist.
    if (!isLg) {
      handleClose();
    }
    openTranscriptPanel(conversationId);
  };

  const sortLabels: Record<SortOption, string> = {
    newest: lang.get('msg.sortNewestFirst'),
    oldest: lang.get('msg.sortOldestFirst'),
    respondent: lang.get('msg.sortRespondentNumber'),
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      // modal={false} removes the backdrop overlay so the main page
      // stays fully visible (and interactive) while the panel is open
      // — matching how the RA artifact panel behaves. Without this,
      // shadcn's default SheetOverlay dims everything behind the panel.
      modal={false}
    >
      <SheetContent
        side="right"
        className="w-full sm:w-[50%] sm:min-w-[500px] p-0 flex flex-col gap-0 [&>button:first-child]:hidden"
        style={effectiveContentStyle}
      >
        <SheetTitle className="sr-only">{lang.get('msg.responsesPanelTitle')}</SheetTitle>

        {/* Resize handle — left edge. Hidden below lg since the Sheet
            goes full-width there. Keyboard users can use arrow keys. */}
        {isLg && (
          <div
            role="separator"
            aria-label="Resize responses panel"
            aria-orientation="vertical"
            aria-valuenow={panelWidth}
            aria-valuemin={PANEL_MIN_WIDTH}
            aria-valuemax={PANEL_MAX_WIDTH}
            tabIndex={0}
            onMouseDown={handleResizeStart}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setPanelWidth((w) => {
                  const next = clampPanelWidth(w + 16);
                  try {
                    window.localStorage.setItem(PANEL_WIDTH_KEY, String(next));
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setPanelWidth((w) => {
                  const next = clampPanelWidth(w - 16);
                  try {
                    window.localStorage.setItem(PANEL_WIDTH_KEY, String(next));
                  } catch {
                    /* ignore */
                  }
                  return next;
                });
              }
            }}
            className="absolute -left-1 top-0 bottom-0 w-2 cursor-col-resize z-20 group focus:outline-none"
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-neutral-200 group-hover:bg-primary group-hover:w-0.5 group-focus-visible:bg-primary/50 group-focus-visible:w-0.5 transition-colors" />
          </div>
        )}

        {/* Header */}
        <div className="border-b border-neutral-200 px-5 py-4 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-neutral-400" />
              <h2 className="text-sm font-semibold text-neutral-900">
                {lang.get('msg.responses')}
              </h2>
              {totalItems > 0 && <span className="text-xs text-neutral-400">({totalItems})</span>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-7 w-7 p-0 text-neutral-400 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Controls row: sort + translation + search. Category chips
              live in a separate row below. Wraps on narrow widths. */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Translation language selector — full feature: switch
                between translated languages, trigger new translations,
                delete existing ones, and poll progress. Orchestrated
                by useQuestionTranslation so completion invalidates the
                other per-question and transcripts caches. */}
            <TranscriptsTranslationDropdown
              isLoading={isTranslating}
              activeLanguage={pendingLanguage || activeLanguage}
              handleLanguageSelection={handleLanguageSelection}
              translatedLanguages={allTranslatedLanguages}
              unTranslatedLanguages={unTranslatedLanguages}
              onDeleteLanguage={handleDeleteLanguage}
              pendingLanguage={pendingLanguage}
              hideLabel
            />

            {/* Sort.
                E2 / audit F-3: a full Radix DropdownMenu migration is
                planned separately. For this pass we add the missing
                ARIA semantics + Escape/click-outside dismissal so
                screen readers and keyboard users can still operate
                the menu. The sort options receive role=menuitem with
                aria-checked so the active option is announced. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSortMenu(!showSortMenu)}
                aria-haspopup="menu"
                aria-expanded={showSortMenu}
                className="h-8 text-xs border border-neutral-200 rounded-lg px-3 bg-white text-neutral-700 hover:bg-neutral-50 flex items-center gap-1.5"
              >
                {sortLabels[sortBy]}
                <ChevronDown className="h-3 w-3 text-neutral-400" />
              </button>
              {showSortMenu && (
                <div
                  role="menu"
                  className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setShowSortMenu(false);
                    }
                  }}
                >
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      role="menuitemradio"
                      aria-checked={sortBy === key}
                      type="button"
                      onClick={() => {
                        setSortBy(key);
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 ${sortBy === key ? 'text-primary font-medium' : 'text-neutral-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="flex-1 min-w-[180px] relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={lang.get('msg.searchResponses')}
                className="w-full h-8 text-xs border border-neutral-200 rounded-lg pl-8 pr-3 bg-white text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Category filter chips — multi-select via toggle. Rendered
              only when the question has categories. Chip styling
              matches the inline ResponsesZone so the two views feel
              consistent. A visible label sits in front of the chip
              row so the row reads as a filter affordance, not just
              decoration — the previous unlabeled chips were getting
              missed by users coming from the pre-redesign inline
              MultiSelect (TELV1-292). */}
          {allCategoryOptions.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <span className="text-[11px] font-medium text-neutral-500 mr-1 shrink-0">
                {lang.get('msg.filterByCategory')}:
              </span>
              {allCategoryOptions.map((opt) => {
                const isSelected = selectedCategories.some((c) => c.label === opt.label);
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedCategories(
                          selectedCategories.filter((c) => c.label !== opt.label),
                        );
                      } else {
                        setSelectedCategories([...selectedCategories, opt]);
                      }
                    }}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-primary/5 border-primary/30 text-primary'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Active filter summary — gives a one-click escape if the
              selected categories aren't finding what the user wanted. */}
          {selectedCategories.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px] text-neutral-500">
                {lang.get('msg.filtered') || 'Filtered by'}:{' '}
                <span className="text-neutral-700 font-medium">
                  {selectedCategories.map((c) => c.label).join(', ')}
                </span>
              </span>
              <button
                type="button"
                onClick={clearCategories}
                className="text-[11px] text-neutral-500 hover:text-neutral-700 underline"
              >
                {lang.get('msg.clearFilter') || 'Clear'}
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : isResponsesError ? (
            // A failed fetch must not resolve into a confident "No
            // responses found" (TELV1-544).
            <FetchErrorState onRetry={() => refetchResponses()} />
          ) : pageTranscripts.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">
              {lang.get('msg.noResponsesFound')}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                {pageTranscripts.map((transcript) => (
                  <ResponseCard
                    key={transcript.conversation_id}
                    transcript={transcript}
                    categoryData={categoryData}
                    questionId={questionId ?? undefined}
                    projectId={projectId}
                    onOpenTranscript={handleOpenTranscript}
                  />
                ))}
              </div>

              {/* Pagination controls. Hidden when everything fits on
                  one page. Server-driven totals — `pagination.totalItems`
                  reflects all pages of the current filter/sort/search
                  query. */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-4 mt-5 pt-4 border-t border-neutral-100">
                  <span className="text-xs text-neutral-500">
                    {totalItems} {lang.get('msg.responses')}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={!pagination?.hasPreviousPage}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      aria-label="Previous page"
                    >
                      <ChevronDown className="h-4 w-4 rotate-90" />
                    </Button>
                    <span className="text-xs text-neutral-600 tabular-nums">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={!pagination?.hasNextPage}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      aria-label="Next page"
                    >
                      <ChevronDown className="h-4 w-4 -rotate-90" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ResponsesPanel;
